/**
 * Matter.js world for falling bottles. One engine, no bottle–bottle collision:
 * each bottle has a private static floor at its pseudo-3D restY.
 */
import { BOTTLE_CLICKER, butelkomatRect } from "@ziolo/shared";
import Matter from "matter-js";
import { DEFAULT_BOTTLE_PHYSICS, type BottlePhysicsTune } from "./bottlePhysics.js";

const { Engine, Bodies, Body, Composite, Events, Sleeping } = Matter;

type SimBody = Matter.Body & { deltaTime: number };

/** Sprite UV inset used as the collider (no pixel scan). */
export const COLLIDER_INSET = { u0: 0.16, v0: 0.06, u1: 0.84, v1: 0.93 };

const STEP_MS = 1000 / 60;
/** Matter linear/angular velocity is px or rad per 16.67ms. */
const TO_MATTER = STEP_MS / 1000;
const MAX_STEPS = 3;
const FLOOR_THICK = 48;
const FIELD_W = BOTTLE_CLICKER.FIELD_WIDTH;
const BOTTLE_CAT = 0x0001;
const CABINET_CAT = 0x0002;
/** Same parking-lot line as BottleField — the box stops here so it cannot pinch bottles. */
const MACHINE_FLOOR_FRAC = 0.81;
const MAX_UP_PX = 320;
const MAX_SPEED_PX = 820;

export interface BottleMatterPose {
  x: number;
  y: number;
  rot: number;
  vx: number;
  vy: number;
  spin: number;
  landed: boolean;
  asleep: boolean;
}

export interface BottleMatterSpawn {
  id: number;
  x: number;
  y: number;
  width: number;
  height: number;
  depthScale: number;
  rot: number;
  vx: number;
  vy: number;
  spin: number;
  weight: number;
  restY: number;
  /** Collide with the butelkomat when this bottle lives on the same depth layer. */
  hitsCabinet: boolean;
}

interface BottleMatterPair {
  body: Matter.Body;
  floor: Matter.Body;
  restY: number;
  width: number;
  height: number;
  colW: number;
  colH: number;
  weight: number;
  comOffset: number;
  didBounce: boolean;
  hitsCabinet: boolean;
  /** True while this bottle is in an active Matter pair (floor or cabinet). */
  touching: boolean;
  /** Sticky floor grip once a real face is down, so the wobble/stick threshold does not chatter. */
  stickFace: boolean;
}

export interface BottleMatterWorld {
  engine: Matter.Engine;
  pairs: Map<number, BottleMatterPair>;
  accMs: number;
  tune: BottlePhysicsTune;
}

export function colliderSize(width: number, height: number, depthScale: number): { w: number; h: number } {
  return {
    w: Math.max(8, (COLLIDER_INSET.u1 - COLLIDER_INSET.u0) * width * depthScale),
    h: Math.max(8, (COLLIDER_INSET.v1 - COLLIDER_INSET.v0) * height * depthScale),
  };
}

export function matterGravityScale(gravityPx: number): number {
  return Math.max(0, gravityPx) / (STEP_MS * 1000);
}

function wrapPi(angle: number): number {
  let t = (angle + Math.PI) % (2 * Math.PI);
  if (t < 0) t += 2 * Math.PI;
  return t - Math.PI;
}

const REST_FACES = [0, Math.PI / 2, -Math.PI / 2, Math.PI] as const;
const REST_FACES_NO_CAP = [0, Math.PI / 2, -Math.PI / 2] as const;

/** Signed angle to the nearest axis-aligned rest (stand, side, or cap). */
function restFaceError(rot: number, allowCap = true): number {
  const a = wrapPi(rot);
  const faces = allowCap ? REST_FACES : REST_FACES_NO_CAP;
  let best = wrapPi(faces[0] - a);
  for (let i = 1; i < faces.length; i += 1) {
    const face = faces[i];
    if (face === undefined) continue;
    const err = wrapPi(face - a);
    if (Math.abs(err) < Math.abs(best)) best = err;
  }
  return best;
}

function isOnCap(rot: number): boolean {
  return Math.abs(wrapPi(rot)) > Math.PI * 0.62;
}

function nearRestFace(rot: number, pad = 0.16, allowCap = true): boolean {
  return Math.abs(restFaceError(rot, allowCap)) < pad;
}

/** Cans are short enough that floor contact + cap torque pumps a blender spin. */
function squatBottle(slot: BottleMatterPair): boolean {
  return slot.colH / Math.max(8, slot.colW) < 1.55;
}

/** Back-row cans are smaller, so the same torque is a much bigger angular kick. */
function squatGroundDamp(slot: BottleMatterPair, baseDamp: number): number {
  const size = Math.min(slot.colW, slot.colH);
  const shrink = Math.max(0, Math.min(1, (52 - size) / 36));
  return baseDamp * (1 - 0.5 * shrink);
}

function faceGrip(tune: BottlePhysicsTune): { friction: number; frictionStatic: number; sleepThreshold: number } {
  return {
    friction: Math.max(0, Math.min(1, tune.slideDamp / 16)),
    frictionStatic: Math.max(0, Math.min(2.5, tune.faceDamp / 8)),
    sleepThreshold: Math.max(1, Math.round(tune.sleepStill * 60)),
  };
}

/** Sticky corners freeze a mid-wobble tilt. Loosen until a real face is down. */
function wobbleGrip(tune: BottlePhysicsTune, squat: boolean): { friction: number; frictionStatic: number; sleepThreshold: number } {
  const grip = faceGrip(tune);
  return {
    friction: grip.friction * (squat ? 0.55 : 0.32),
    frictionStatic: grip.frictionStatic * (squat ? 0.26 : 0.1),
    sleepThreshold: 180,
  };
}

function comOffsetFor(height: number, tipGravity: number): number {
  const t = Math.max(0, Math.min(1, tipGravity / 2000));
  return height * 0.18 * (1 - t * 0.85);
}

function bottleBody(pair: Matter.Pair): Matter.Body | null {
  if (pair.bodyA.label === "bottle") return pair.bodyA;
  if (pair.bodyB.label === "bottle") return pair.bodyB;
  return null;
}

function lookUp(world: BottleMatterWorld, body: Matter.Body): BottleMatterPair | undefined {
  const id = Number((body.plugin as { bottleId?: number }).bottleId);
  if (!Number.isFinite(id)) return undefined;
  return world.pairs.get(id);
}

/** Matter zeros `body.totalContacts` at the end of position solve, so we track pairs ourselves. */
function refreshTouching(world: BottleMatterWorld): void {
  for (const slot of world.pairs.values()) slot.touching = false;
  const list = (world.engine.pairs as { list?: Matter.Pair[] }).list;
  if (!list) return;
  for (const pair of list) {
    if (!pair.isActive) continue;
    const body = bottleBody(pair);
    if (!body) continue;
    const slot = lookUp(world, body);
    if (slot) slot.touching = true;
  }
}

function airForWeight(airDamp: number, weight: number): number {
  const airBase = Math.max(0, Math.min(0.12, airDamp / 50));
  const airScale = 1 / Math.sqrt(Math.max(0.28, weight));
  return Math.min(0.14, airBase * airScale);
}

function addCabinet(engine: Matter.Engine): void {
  const machine = butelkomatRect();
  const floorY = machine.y + machine.height * MACHINE_FLOOR_FRAC;
  const h = Math.max(40, floorY - machine.y);
  const inset = 6;
  const cabinet = Bodies.rectangle(
    machine.x + machine.width / 2,
    machine.y + h / 2,
    Math.max(40, machine.width - inset * 2),
    h,
    {
      label: "cabinet",
      isStatic: true,
      restitution: 0.14,
      friction: 0.18,
      frictionStatic: 0.22,
      chamfer: { radius: 12 },
      collisionFilter: { group: 0, category: CABINET_CAT, mask: BOTTLE_CAT },
    },
  );
  Composite.add(engine.world, cabinet);
}

export function createBottleWorld(): BottleMatterWorld {
  const engine = Engine.create({
    enableSleeping: true,
    gravity: { x: 0, y: 1, scale: matterGravityScale(DEFAULT_BOTTLE_PHYSICS.gravity) },
    positionIterations: 6,
  });
  const world: BottleMatterWorld = {
    engine,
    pairs: new Map(),
    accMs: 0,
    tune: { ...DEFAULT_BOTTLE_PHYSICS },
  };
  addCabinet(engine);

  Events.on(engine, "collisionStart", (event: Matter.IEventCollision<Matter.Engine>) => {
    const tune = world.tune;
    for (const pair of event.pairs) {
      const body = bottleBody(pair);
      if (!body) continue;
      const slot = lookUp(world, body);
      if (!slot) continue;
      const speed = Body.getSpeed(body);
      const cutPx = slot.didBounce ? tune.bounceCutLand : tune.bounceCut;
      const cut = cutPx * TO_MATTER;
      Body.set(body, { restitution: speed < cut ? 0 : Math.max(0, Math.min(0.7, tune.bounce)) });
      slot.didBounce = true;
    }
  });

  Events.on(engine, "beforeUpdate", () => {
    const tune = world.tune;
    const g = engine.gravity.scale * engine.gravity.y;
    for (const slot of world.pairs.values()) {
      const { body } = slot;
      const squat = squatBottle(slot);
      if (!slot.touching) {
        slot.stickFace = false;
      } else {
        const err = Math.abs(restFaceError(body.angle, squat));
        if (slot.stickFace) {
          if (err > 0.26) slot.stickFace = false;
        } else if (err < 0.12) {
          slot.stickFace = true;
        }
      }
      Body.set(body, slot.touching && !slot.stickFace ? wobbleGrip(tune, squat) : faceGrip(tune));
      if (body.isSleeping) {
        if (slot.touching && !slot.stickFace) Sleeping.set(body, false);
        else continue;
      }
      if (!slot.touching) {
        body.force.y += (slot.weight - 1) * g * body.mass * 0.7;
      }
      // Cap-recovery rolls tall PET/glass onto a side. Never run it on squat
      // cans — their inertia is tiny in the back row and the torque blender-spins them.
      if (squat || !isOnCap(body.angle)) continue;
      Sleeping.set(body, false);
      const side = body.angle >= 0 ? Math.PI / 2 : -Math.PI / 2;
      const err = wrapPi(side - body.angle);
      body.torque += Math.sign(err || side) * tune.tipGravity * body.mass * 0.05;
    }
  });

  return world;
}

export function addBottle(world: BottleMatterWorld, spawn: BottleMatterSpawn): void {
  const tune = world.tune;
  const { w, h } = colliderSize(spawn.width, spawn.height, spawn.depthScale);
  const cx = spawn.x + spawn.width / 2;
  const cy = spawn.y + spawn.height / 2;
  const group = Math.max(1, spawn.id | 0);
  const filter = {
    group,
    category: BOTTLE_CAT,
    mask: spawn.hitsCabinet ? CABINET_CAT : 0,
  };
  const comOffset = comOffsetFor(h, tune.tipGravity);
  const floorTop = spawn.restY + tune.sitOverlap;
  const squat = h / Math.max(8, w) < 1.55;
  const chamfer = squat
    ? Math.min(1.1, Math.min(w, h) * 0.02)
    : Math.min(3, Math.min(w, h) * 0.04);

  const body = Bodies.rectangle(cx, cy, w, h, {
    label: "bottle",
    chamfer: { radius: chamfer },
    restitution: Math.max(0, Math.min(0.7, tune.bounce)),
    friction: Math.max(0, Math.min(1, tune.slideDamp / 16)),
    frictionStatic: Math.max(0, Math.min(2.5, tune.faceDamp / 8)),
    frictionAir: airForWeight(tune.airDamp, spawn.weight),
    density: 0.001 * Math.max(0.2, spawn.weight),
    sleepThreshold: Math.max(1, Math.round(tune.sleepStill * 60)),
    slop: Math.max(0.05, tune.slop),
    collisionFilter: filter,
    plugin: { bottleId: spawn.id },
  });
  (body as SimBody).deltaTime = STEP_MS;
  Body.setCentre(body, { x: 0, y: comOffset }, true);
  Body.setAngle(body, spawn.rot);
  Body.setVelocity(body, { x: spawn.vx * TO_MATTER, y: spawn.vy * TO_MATTER });
  Body.setAngularVelocity(body, spawn.spin * TO_MATTER);

  const floor = Bodies.rectangle(FIELD_W / 2, floorTop + FLOOR_THICK / 2, FIELD_W * 2, FLOOR_THICK, {
    label: "floor",
    isStatic: true,
    friction: 0.8,
    frictionStatic: 1,
    restitution: 0,
    slop: Math.max(0.05, tune.slop),
    collisionFilter: { group, category: BOTTLE_CAT, mask: 0 },
  });

  Composite.add(world.engine.world, [body, floor]);
  world.pairs.set(spawn.id, {
    body,
    floor,
    restY: spawn.restY,
    width: spawn.width,
    height: spawn.height,
    colW: w,
    colH: h,
    weight: spawn.weight,
    comOffset,
    didBounce: false,
    hitsCabinet: spawn.hitsCabinet,
    touching: false,
    stickFace: false,
  });
}

export function removeBottle(world: BottleMatterWorld, id: number): void {
  const slot = world.pairs.get(id);
  if (!slot) return;
  Composite.remove(world.engine.world, slot.body);
  Composite.remove(world.engine.world, slot.floor);
  world.pairs.delete(id);
}

export function applyTune(world: BottleMatterWorld, tune: BottlePhysicsTune): void {
  world.tune = tune;
  const engine = world.engine;
  engine.gravity.x = 0;
  engine.gravity.y = 1;
  engine.gravity.scale = matterGravityScale(tune.gravity);
  engine.positionIterations = Math.max(2, Math.min(12, Math.round(4 + tune.slop)));

  const restitution = Math.max(0, Math.min(0.7, tune.bounce));
  const friction = Math.max(0, Math.min(1, tune.slideDamp / 16));
  const frictionStatic = Math.max(0, Math.min(2.5, tune.faceDamp / 8));
  const sleepThreshold = Math.max(1, Math.round(tune.sleepStill * 60));
  const slop = Math.max(0.05, tune.slop);

  for (const slot of world.pairs.values()) {
    Body.set(slot.body, {
      restitution,
      friction,
      frictionStatic,
      frictionAir: airForWeight(tune.airDamp, slot.weight),
      sleepThreshold,
      slop,
    });
    slot.floor.slop = slop;
    const floorTop = slot.restY + tune.sitOverlap;
    const floorY = floorTop + FLOOR_THICK / 2;
    if (Math.abs(slot.floor.position.y - floorY) > 0.05) {
      Body.setPosition(slot.floor, { x: FIELD_W / 2, y: floorY });
    }
  }
}

function afterStep(world: BottleMatterWorld): void {
  refreshTouching(world);
  const tune = world.tune;
  const cap = Math.max(0.01, tune.spinCap * TO_MATTER);
  const damp = Math.max(0, 1 - tune.groundDamp * TO_MATTER);
  const maxUp = MAX_UP_PX * TO_MATTER;
  const maxSpd = MAX_SPEED_PX * TO_MATTER;
  for (const slot of world.pairs.values()) {
    const { body } = slot;
    if (body.isSleeping) continue;
    let spin = Body.getAngularVelocity(body);
    if (slot.touching) {
      const speed = Body.getSpeed(body);
      const squat = squatBottle(slot);
      const err = restFaceError(body.angle, squat);
      if (squat) {
        const onFace = Math.abs(err) < 0.18;
        if (onFace) {
          spin *= squatGroundDamp(slot, damp);
          if (speed < 0.5) spin *= 0.35;
        } else if (speed < 1.4 && Math.abs(spin) > cap * 0.22) {
          spin *= 0.62;
        }
        const squatCap = cap * (0.45 + 0.35 * Math.min(1, Math.min(slot.colW, slot.colH) / 48));
        if (Math.abs(spin) > squatCap) spin = Math.sign(spin) * squatCap;
      } else if (nearRestFace(body.angle, 0.2, false)) {
        // Tall bottles keep their landing roll; damp only after they choose a face.
        spin *= damp;
      }
    }
    if (Math.abs(spin) > cap) spin = Math.sign(spin) * cap;
    Body.setAngularVelocity(body, spin);
    const vel = Body.getVelocity(body);
    let { x, y } = vel;
    if (y < -maxUp) y = -maxUp;
    const spd = Math.hypot(x, y);
    if (spd > maxSpd && spd > 0) {
      const s = maxSpd / spd;
      x *= s;
      y *= s;
    }
    if (x !== vel.x || y !== vel.y) Body.setVelocity(body, { x, y });
  }
}

export function stepWorld(world: BottleMatterWorld, dtSec: number): void {
  world.accMs += Math.max(0, dtSec) * 1000;
  let steps = 0;
  while (world.accMs >= STEP_MS && steps < MAX_STEPS) {
    Engine.update(world.engine, STEP_MS);
    afterStep(world);
    world.accMs -= STEP_MS;
    steps += 1;
  }
  if (steps >= MAX_STEPS) world.accMs = 0;
}

export function readPose(world: BottleMatterWorld, id: number): BottleMatterPose | null {
  const slot = world.pairs.get(id);
  if (!slot) return null;
  const { body, comOffset, width, height } = slot;
  const a = body.angle;
  const spriteCx = body.position.x + comOffset * Math.sin(a);
  const spriteCy = body.position.y - comOffset * Math.cos(a);
  const vel = Body.getVelocity(body);
  const spin = Body.getAngularVelocity(body);
  return {
    x: spriteCx - width / 2,
    y: spriteCy - height / 2,
    rot: a,
    vx: vel.x / TO_MATTER,
    vy: vel.y / TO_MATTER,
    spin: spin / TO_MATTER,
    landed: body.isSleeping || slot.touching,
    asleep: body.isSleeping,
  };
}

export function clampSpriteX(world: BottleMatterWorld, id: number, left: number, right: number): void {
  const slot = world.pairs.get(id);
  const pose = readPose(world, id);
  if (!slot || !pose) return;
  let dx = 0;
  if (pose.x < left) dx = left - pose.x;
  else if (pose.x + slot.width > right) dx = right - (pose.x + slot.width);
  if (dx === 0) return;
  Body.setPosition(slot.body, { x: slot.body.position.x + dx, y: slot.body.position.y });
  const vel = Body.getVelocity(slot.body);
  Body.setVelocity(slot.body, { x: -vel.x * 0.4, y: vel.y });
}

export function destroyWorld(world: BottleMatterWorld): void {
  Events.off(world.engine, "collisionStart");
  Events.off(world.engine, "beforeUpdate");
  Composite.clear(world.engine.world, false);
  Engine.clear(world.engine);
  world.pairs.clear();
}
